"use client"

import styled from "@emotion/styled"
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  TextField,
} from "@mui/material"
import { LocalizationProvider, StaticDatePicker } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import dayjs, { Dayjs } from "dayjs"
import "dayjs/locale/he"
import { useTranslations } from "next-intl"
import { useCallback, useEffect, useMemo, useState } from "react"
import { FacilityContent, PortItem } from "utils"
import {
  Calendar3Icon,
  ChairIcon,
  Check2Icon,
  Filter2Icon,
  Location3Icon,
  UserIcon,
  X3Icon,
  XIcon,
} from "../icons"
import ArrowDown2Icon from "../icons/ArrowDown2Icon"
import {
  CalendarWithTimeLayout,
  CustomCalendarHeader,
  StrikethroughPickersDay,
  StyledCalendarTimeRow,
  StyledConfirmButton,
  StyledDatePicker,
  StyledDuration,
  StyledMobileCalendar,
  TimePickerContext,
  datePickerSlotProps,
} from "./CalendarWithTimeLayout"
import {
  EMPTY_FILTERS,
  START_TIME_SLOTS,
  SearchFilters,
  TIME_SLOTS,
  formatDurationHebrew,
  isMeetingRoom,
  isOffice,
} from "./constants"
import InlineTimeDropdown from "./InlineTimeDropdown"
import SelectWithIcon from "./SelectWithIcon"
import {
  ComingSoonBadge,
  MultiSelectCheckbox,
  MultiSelectMenuItem,
  StyledClearIcon,
  StyledFieldIcon,
  StyledResultsCount,
} from "./shared"

const StyledSearchBar = styled.div<{ $hasError?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding: 20px;
  border: 1px solid ${(props) => (props.$hasError ? "red" : props.theme.colors.port.grey)};
  border-radius: 12px;
  background: ${(props) => props.theme.colors.white};

  .MuiFormControl-root {
    flex: 1;
    min-width: 0;
  }

  .MuiInputLabel-root {
    position: relative;
    transform: none;
    color: ${(props) => props.theme.colors.port.defaultTextColor};
    font-size: 16px;
    font-weight: 300;
    line-height: 150%;
    margin-bottom: 4px;
    pointer-events: auto;
    &.Mui-focused {
      color: ${(props) => props.theme.colors.port.defaultTextColor};
    }
    &.MuiInputLabel-shrink {
      transform: none;
    }
  }
`

const StyledNoDateMessage = styled.div`
  font-size: 16px;
  font-weight: 300;
  color: #687079;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  padding: 8px 12px;
  border: 1.2px solid #e6e6e6;
  border-radius: 12px;
  background: linear-gradient(90deg, rgba(230, 230, 230, 0.4) 0%, rgba(230, 230, 230, 0.4) 100%),
    linear-gradient(90deg, #fff 0%, #fff 100%);
`

const StyledSearchButton = styled(Button)<{ $active: boolean }>`
  && {
    height: 48px;
    min-width: 124px;
    border-radius: 45px;
    font-size: 18px;
    font-weight: 700;
    text-transform: none;
    text-decoration: none;
    box-shadow: none;
    background-color: ${({ $active, theme }) =>
      $active ? theme.colors.propBlue : theme.colors.port.portBulletColor};
    color: ${(props) => props.theme.colors.white};
    cursor: pointer;

    &:hover {
      background-color: ${({ $active, theme }) =>
        $active ? theme.colors.buttonHoverBackgroundColor : theme.colors.port.portBulletColor};
      box-shadow: none;
      text-decoration: none;
    }

    &:active {
      background-color: ${({ $active, theme }) =>
        $active ? theme.colors.buttonActiveBackgroundColor : theme.colors.port.portBulletColor};
    }
  }
`

const StyledClearButton = styled(Button)`
  display: flex;
  font-size: 16px;
  font-weight: 300;
  text-transform: none;
  color: ${(props) => props.theme.colors.port.defaultTextColor};
  text-decoration: underline;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: none;
  }
`

const StyledErrorMessage = styled.p`
  margin: 0;
  font-size: 14px;
  color: red;
`

const StyledMobileFilterRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  ${(props) => props.theme.breakpoints.up("md")} {
    display: none;
  }
`

const StyledMobile = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: none;
  }
`

const StyledFilterButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 99px;
  height: 37px;
  border: 1px solid #0e2846;
  border-radius: 45px;
  background: transparent;
  cursor: pointer;
  padding: 0;

  span {
    font-size: 14px;
    font-weight: 700;
    color: #0e2846;
    font-family: inherit;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`

const StyledFilterDot = styled.span`
  width: 12px;
  height: 12px;
  border: 1px solid white;
  border-radius: 50%;
  background-color: #0000fa;
  position: absolute;
  top: -5px;
  inset-inline-start: 10px;
`

const StyledDrawerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1300;
`

const StyledDrawer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background: #fff;
  border-radius: 16px 16px 0 0;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.16);
  display: flex;
  flex-direction: column;
  height: 583px;
  z-index: 1301;
`

const StyledDrawerHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 16px;
`

const StyledCloseButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #fff;
  border: none;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  svg {
    width: 14px;
    height: 14px;
  }
`

const StyledDrawerTitle = styled.span`
  font-size: 20px;
  font-weight: 300;
  letter-spacing: 0.6px;
  flex: 1;
  color: #0e2846;
  font-family: var(--simpler-pro-full) !important;
`

const StyledDrawerScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 16px;
`

const StyledAccordionItem = styled.div<{ $disabled?: boolean }>`
  border-bottom: 1px solid #e6e6e6;
  padding: 8px;
  ${({ $disabled }) =>
    $disabled &&
    `
    background: linear-gradient(90deg, rgba(230, 230, 230, 0.4) 0%, rgba(230, 230, 230, 0.4) 100%),
      linear-gradient(90deg, #fff 0%, #fff 100%);
    border-radius: 12px;
    border-bottom: none;  
    position: relative;
    &::after {
      content: "";
      position: absolute;
      bottom: -8px;
      left: -8px;
      right: -8px;
      height: 1px;
      background: #e6e6e6;
    }
  `}
`

const StyledAccordionHeader = styled.button<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 0;
  background: transparent;
  border: none;
  cursor: pointer;

  .accordion-label {
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
      width: 20px;
      height: 20px;
      color: #687079;
    }

    span {
      font-size: 16px;
      font-weight: 300;
      color: #0e2846;
      font-family: var(--simpler-pro-full) !important;
    }
  }

  .accordion-arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s ease;
    transform: ${({ $expanded }) => ($expanded ? "rotate(-180deg)" : "rotate(0deg)")};

    svg {
      width: 24px;
      height: 24px;
    }
  }
`

const StyledAccordionContent = styled.div`
  padding-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const StyledDrawerCTA = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  padding: 16px;
  background: #fff;
  box-shadow: 11px 0px 12.2px rgba(0, 0, 0, 0.15);
`

const StyledDrawerSearchButton = styled.button<{ $active: boolean }>`
  flex: 2;
  height: 40px;
  border-radius: 45px;
  border: none;
  background-color: ${({ $active }) => ($active ? "#0000fa" : "#687079")};
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
`

const StyledDrawerClearButton = styled.button`
  flex: 1;
  height: 40px;
  border-radius: 45px;
  border: 1px solid #0e2846;
  background: transparent;
  color: #0e2846;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
`

const StyledMobileCheckboxItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 8px;
  cursor: pointer;

  span.label-text {
    font-size: 16px;
    font-weight: 300;
    font-family: var(--simpler-pro-full) !important;
    color: #2a2a2a;
    display: flex;
    align-items: center;
    gap: 4px;
    flex: 1;
    & * {
      font-family: inherit !important;
    }
  }
`

const StyledTimeField = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const StyledTimeLabel = styled.span`
  font-size: 16px;
  font-weight: 300;
  color: #0e2846;
  line-height: 1.5;
`

type FacilitiesSearchBarProps = {
  ports: PortItem[]
  allFacilities: FacilityContent[]
  // eslint-disable-next-line no-unused-vars
  onSearch: (filters: SearchFilters) => void
  hasSearched: boolean
  filteredCount: number
  hostAvailability?: {
    // eslint-disable-next-line no-unused-vars
    shouldDisableDate?: (date: any) => boolean
    disabledTimeSlots: Set<string>
  }
}

function FacilitiesSearchBar({
  ports,
  allFacilities,
  onSearch,
  filteredCount,
  hostAvailability,
}: FacilitiesSearchBarProps) {
  const t = useTranslations("portFacilitiesInnerPage")
  const [filters, setFilters] = useState<SearchFilters>(EMPTY_FILTERS)
  const [showError, setShowError] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [expandedSection, setExpandedSection] = useState<string | null>(null)
  const [datePickerOpen, setDatePickerOpen] = useState(false)

  const locations = useMemo(() => {
    return ports
      .filter((p) => p.portName && !p.upperTextOurLocation?.toLowerCase().includes("coming soon"))
      .map((p) => ({
        name: p.portName!,
        disabled: false,
      }))
  }, [ports])

  const facilityTypes = useMemo(() => {
    return Array.from(new Set(allFacilities.map((f) => f.facilityType).filter(Boolean)))
  }, [allFacilities])

  const peopleOptions = useMemo(() => {
    return Array.from(new Set(allFacilities.map((f) => f.amountOfPeople).filter(Boolean)))
  }, [allFacilities])

  const isOnlyOfficesSelected =
    filters.facilityType.length > 0 && filters.facilityType.every((ft) => isOffice(ft))

  const isMeetingRoomSelected =
    filters.facilityType.length > 0 && filters.facilityType.some((ft) => isMeetingRoom(ft))

  const duration =
    filters.startTime && filters.endTime
      ? formatDurationHebrew(filters.startTime, filters.endTime)
      : ""

  const dateDisplayValue = useMemo(() => {
    if (!filters.date) return ""
    const dateStr = dayjs(filters.date).format("DD.MM.YYYY")
    if (isMeetingRoomSelected && filters.startTime) {
      const timeStr = filters.endTime
        ? `${filters.startTime}-${filters.endTime}`
        : filters.startTime
      return `${dateStr} , ${timeStr}`
    }
    return dateStr
  }, [filters.date, filters.startTime, filters.endTime, isMeetingRoomSelected])

  const disabledTimeSlots = isMeetingRoomSelected ? hostAvailability?.disabledTimeSlots : undefined

  const endTimeSlots = useMemo(() => {
    if (!filters.startTime) return TIME_SLOTS
    return TIME_SLOTS.filter((t) => t > filters.startTime)
  }, [filters.startTime])

  const hasAnyFilter =
    filters.location.length > 0 ||
    filters.facilityType.length > 0 ||
    filters.date ||
    filters.startTime ||
    filters.endTime ||
    filters.numberOfPeople.length > 0

  const handleSearch = useCallback(() => {
    if (!hasAnyFilter) {
      setShowError(true)
      return
    }
    setShowError(false)
    onSearch(filters)
  }, [filters, hasAnyFilter, onSearch])

  const clearAndSearch = useCallback(
    (updatedFilters: SearchFilters) => {
      setFilters(updatedFilters)
      setShowError(false)
      onSearch(updatedFilters)
    },
    [onSearch],
  )

  const handleClear = useCallback(() => {
    clearAndSearch(EMPTY_FILTERS)
  }, [clearAndSearch])

  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [drawerOpen])

  useEffect(() => {
    if (filters.startTime && filters.endTime && filters.endTime <= filters.startTime) {
      setFilters((prev) => ({ ...prev, endTime: "" }))
    }
  }, [filters.startTime, filters.endTime])

  useEffect(() => {
    if (!isMeetingRoomSelected && (filters.startTime || filters.endTime)) {
      setFilters((prev) => ({ ...prev, startTime: "", endTime: "" }))
    }
  }, [isMeetingRoomSelected, filters.startTime, filters.endTime])

  const toggleAccordion = (section: string) => {
    setExpandedSection((prev) => (prev === section ? null : section))
  }

  const handleMobileCheckbox = (
    field: "location" | "numberOfPeople",
    value: string,
    disabled?: boolean,
  ) => {
    if (disabled) return
    setFilters((prev) => {
      const arr = prev[field] as string[]
      const next = arr.includes(value) ? [] : [value]
      return { ...prev, [field]: next }
    })
    setShowError(false)
  }

  const handleMobileFacilityType = (value: string) => {
    setFilters((prev) => {
      const next = prev.facilityType.includes(value) ? [] : [value]
      return { ...prev, facilityType: next }
    })
    setShowError(false)
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {/* Desktop search bar */}
      <Box sx={{ display: { xs: "none", md: "flex" }, flexDirection: "column", gap: "12px" }}>
        <StyledSearchBar $hasError={showError}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: "12px", md: "24px" },
              alignItems: { md: "flex-end" },
              width: "100%",
            }}
          >
            <FormControl variant="standard" sx={{ flex: 1, minWidth: { md: 237 } }}>
              <InputLabel shrink>{t("where")}</InputLabel>
              <Autocomplete
                options={locations}
                getOptionLabel={(option) => option.name}
                getOptionDisabled={(option) => option.disabled}
                value={
                  filters.location.length > 0
                    ? locations.find((l) => l.name === filters.location[0]) || null
                    : null
                }
                onChange={(_, newValue) => {
                  const updated = { ...filters, location: newValue ? [newValue.name] : [] }
                  if (!newValue) {
                    clearAndSearch(updated)
                  } else {
                    setFilters(updated)
                    setShowError(false)
                  }
                }}
                noOptionsText=""
                popupIcon={null}
                clearIcon={filters.location.length > 0 ? <XIcon color="#0e2846" /> : null}
                renderOption={(props, option) => {
                  const isSelected = filters.location.includes(option.name)
                  return (
                    <MultiSelectMenuItem
                      {...props}
                      key={option.name}
                      sx={
                        option.disabled
                          ? { color: "#687079 !important", opacity: "1 !important" }
                          : {}
                      }
                    >
                      <MultiSelectCheckbox $isSelected={isSelected} $disabled={option.disabled}>
                        {isSelected && <Check2Icon />}
                      </MultiSelectCheckbox>
                      <span style={{ display: "flex", alignItems: "center", gap: 4, flex: 1 }}>
                        {option.disabled && <ComingSoonBadge>{t("comingSoon")}</ComingSoonBadge>}
                        <span style={option.disabled ? { color: "#687079" } : undefined}>
                          {option.name}
                        </span>
                      </span>
                    </MultiSelectMenuItem>
                  )
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t("selectLocation")}
                    variant="standard"
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      startAdornment: !filters.location.length ? (
                        <Location3Icon color="#687079" />
                      ) : undefined,
                    }}
                  />
                )}
                sx={{
                  "& .MuiInputBase-root": {
                    backgroundColor: "white",
                    border: "1px solid #e4e4e4",
                    borderRadius: "8px",
                    height: "48px",
                    padding: "0 12px !important",
                    marginTop: "0 !important",
                    display: "flex",
                    alignItems: "center",
                    "&:hover": { backgroundColor: "white" },
                    "&.Mui-focused": { backgroundColor: "white", borderColor: "#0e2846" },
                    "&:before, &:after": { display: "none !important" },
                  },
                  "& .MuiInputBase-input": {
                    padding: "0 !important",
                    fontSize: "16px",
                    color: filters.location.length > 0 ? "#0E2846" : "#687079",
                    fontWeight: filters.location.length > 0 ? 700 : 300,
                    "&::placeholder": {
                      color: "#687079",
                      fontWeight: 300,
                      opacity: 1,
                    },
                  },
                  "& .MuiAutocomplete-endAdornment": {
                    position: "static",
                    display: "flex",
                    alignItems: "center",
                  },
                }}
              />
            </FormControl>

            <FormControl variant="standard" sx={{ flex: 1 }}>
              <InputLabel shrink>{t("facilityType")}</InputLabel>
              <SelectWithIcon
                displayEmpty
                multiple
                value={filters.facilityType}
                onChange={(e: SelectChangeEvent<string[]>) => {
                  const val = e.target.value
                  const arr = typeof val === "string" ? val.split(",") : val
                  const newVal =
                    arr.length > 1 ? arr.filter((v) => !filters.facilityType.includes(v)) : arr
                  setFilters((prev) => ({ ...prev, facilityType: newVal.slice(0, 1) }))
                  setShowError(false)
                }}
                onClear={() => {
                  clearAndSearch({ ...filters, facilityType: [] })
                }}
                defaultIcon={ChairIcon}
                placeholder={t("selectType")}
              >
                {facilityTypes.map((type) => {
                  const isSelected = filters.facilityType.includes(type)
                  return (
                    <MultiSelectMenuItem key={type} value={type}>
                      <MultiSelectCheckbox $isSelected={isSelected}>
                        {isSelected && <Check2Icon />}
                      </MultiSelectCheckbox>
                      {type}
                    </MultiSelectMenuItem>
                  )
                })}
              </SelectWithIcon>
            </FormControl>

            <FormControl variant="standard" sx={{ flex: 1 }}>
              <InputLabel shrink sx={isOnlyOfficesSelected ? { color: "#687079" } : undefined}>
                {t("when")}
              </InputLabel>
              {isOnlyOfficesSelected ? (
                <StyledNoDateMessage>
                  {t("noDateNeeded")}
                  <Calendar3Icon color="#687079" />
                </StyledNoDateMessage>
              ) : (
                <TimePickerContext.Provider
                  value={
                    isMeetingRoomSelected
                      ? {
                          startTime: filters.startTime,
                          endTime: filters.endTime,
                          startTimeSlots: START_TIME_SLOTS,
                          endTimeSlots,
                          disabledTimeSlots,
                          duration,
                          onStartTimeChange: (val) => {
                            setFilters((prev) => ({ ...prev, startTime: val }))
                            setShowError(false)
                          },
                          onEndTimeChange: (val) => {
                            setFilters((prev) => ({ ...prev, endTime: val }))
                            setShowError(false)
                          },
                          onConfirm: () => setDatePickerOpen(false),
                        }
                      : null
                  }
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="he">
                    <Box sx={{ position: "relative" }}>
                      <StyledDatePicker
                        dayOfWeekFormatter={(day: string) => `${day.replace(/׳/g, "")}׳`}
                        value={filters.date ? dayjs(filters.date) : null}
                        onChange={(date: any) => {
                          setFilters((prev) => ({
                            ...prev,
                            date: date ? (date as Dayjs).format("YYYY-MM-DD") : "",
                          }))
                          setShowError(false)
                          if (!isMeetingRoomSelected) setDatePickerOpen(false)
                        }}
                        open={datePickerOpen}
                        onOpen={() => setDatePickerOpen(true)}
                        onClose={() => setDatePickerOpen(false)}
                        minDate={dayjs()}
                        shouldDisableDate={
                          isMeetingRoomSelected ? hostAvailability?.shouldDisableDate : undefined
                        }
                        format="DD/MM/YYYY"
                        slots={{
                          openPickerIcon: () => null,
                          calendarHeader: CustomCalendarHeader,
                          ...(isMeetingRoomSelected
                            ? { layout: CalendarWithTimeLayout, day: StrikethroughPickersDay }
                            : {}),
                        }}
                        slotProps={{
                          ...datePickerSlotProps,
                          textField: {
                            ...datePickerSlotProps.textField,
                            onClick: () => setDatePickerOpen(true),
                            inputProps: {
                              ...datePickerSlotProps.textField.inputProps,
                              ...(dateDisplayValue ? { value: dateDisplayValue } : {}),
                              style: {
                                ...datePickerSlotProps.textField.inputProps.style,
                                fontWeight: filters.date ? 700 : 300,
                              },
                            },
                          },
                          ...(isMeetingRoomSelected
                            ? {
                                popper: {
                                  ...datePickerSlotProps.popper,
                                  sx: {
                                    ...datePickerSlotProps.popper.sx,
                                    "& .MuiPaper-root": {
                                      ...datePickerSlotProps.popper.sx["& .MuiPaper-root"],
                                      height: "auto !important",
                                    },
                                  },
                                },
                              }
                            : {}),
                        }}
                      />
                      {filters.date ? (
                        <StyledClearIcon
                          onMouseDown={(e: any) => {
                            e.stopPropagation()
                            e.preventDefault()
                            clearAndSearch({ ...filters, date: "", startTime: "", endTime: "" })
                          }}
                        >
                          <XIcon color="#0e2846" />
                        </StyledClearIcon>
                      ) : (
                        <StyledFieldIcon>
                          <Calendar3Icon color="#687079" />
                        </StyledFieldIcon>
                      )}
                    </Box>
                  </LocalizationProvider>
                </TimePickerContext.Provider>
              )}
            </FormControl>

            <FormControl variant="standard" sx={{ flex: 1 }}>
              <InputLabel shrink>{t("howManyPeople")}</InputLabel>
              <SelectWithIcon
                displayEmpty
                multiple
                value={filters.numberOfPeople}
                onChange={(e: SelectChangeEvent<string[]>) => {
                  const val = e.target.value
                  const arr = typeof val === "string" ? val.split(",") : val
                  const newVal =
                    arr.length > 1 ? arr.filter((v) => !filters.numberOfPeople.includes(v)) : arr
                  setFilters((prev) => ({ ...prev, numberOfPeople: newVal.slice(0, 1) }))
                  setShowError(false)
                }}
                onClear={() => {
                  clearAndSearch({ ...filters, numberOfPeople: [] })
                }}
                defaultIcon={() => <UserIcon color="#687079" />}
                placeholder={t("selectAmount")}
                options={peopleOptions}
              />
            </FormControl>

            <StyledSearchButton
              $active={!!hasAnyFilter}
              onClick={handleSearch}
              variant="contained"
              disableElevation
              disableRipple={!hasAnyFilter}
            >
              {t("search")}
            </StyledSearchButton>
          </Box>
          {showError && <StyledErrorMessage>{t("searchError")}</StyledErrorMessage>}
        </StyledSearchBar>

        <StyledClearButton onClick={handleClear}>{t("clearAll")}</StyledClearButton>
      </Box>

      <StyledMobile>
        {/* Mobile filter button row */}
        <StyledMobileFilterRow>
          {filteredCount > 0 ? (
            <StyledResultsCount>
              {t("found")} <span>{filteredCount}</span>{" "}
              {filters.facilityType.length > 0 ? filters.facilityType[0] : t("searchResults")}
            </StyledResultsCount>
          ) : (
            <span />
          )}
          <StyledFilterButton onClick={() => setDrawerOpen(true)}>
            <span>{t("filters")}</span>
            <Filter2Icon style={{ width: 18, height: 18 }} />
            {hasAnyFilter && <StyledFilterDot />}
          </StyledFilterButton>
        </StyledMobileFilterRow>

        {/* Mobile Filter Drawer */}
        {drawerOpen && (
          <>
            <StyledDrawerOverlay onClick={() => setDrawerOpen(false)} />
            <StyledDrawer>
              <StyledDrawerHeader>
                <StyledDrawerTitle>{t("filters")}</StyledDrawerTitle>
                <StyledCloseButton onClick={() => setDrawerOpen(false)}>
                  <X3Icon color="#0e2846" />
                </StyledCloseButton>
              </StyledDrawerHeader>

              <StyledDrawerScroll>
                <StyledAccordionItem>
                  <StyledAccordionHeader
                    $expanded={expandedSection === "location"}
                    onClick={() => toggleAccordion("location")}
                  >
                    <div className="accordion-label">
                      <Location3Icon color="#687079" />
                      <span>{t("where")}</span>
                      {filters.location.length > 0 && (
                        <span style={{ fontWeight: 700 }}>{filters.location[0]}</span>
                      )}
                    </div>
                    <div className="accordion-arrow">
                      <ArrowDown2Icon />
                    </div>
                  </StyledAccordionHeader>
                  {expandedSection === "location" && (
                    <StyledAccordionContent>
                      {locations.map(({ name, disabled }) => {
                        const isSelected = filters.location.includes(name)
                        return (
                          <StyledMobileCheckboxItem
                            key={name}
                            onClick={() => handleMobileCheckbox("location", name, disabled)}
                            style={disabled ? { opacity: 0.6, cursor: "default" } : undefined}
                          >
                            <MultiSelectCheckbox $isSelected={isSelected} $disabled={disabled}>
                              {isSelected && <Check2Icon />}
                            </MultiSelectCheckbox>
                            <span className="label-text">
                              {disabled && <ComingSoonBadge>{t("comingSoon")}</ComingSoonBadge>}
                              <span style={disabled ? { color: "#687079" } : undefined}>
                                {name}
                              </span>
                            </span>
                          </StyledMobileCheckboxItem>
                        )
                      })}
                    </StyledAccordionContent>
                  )}
                </StyledAccordionItem>

                <StyledAccordionItem>
                  <StyledAccordionHeader
                    $expanded={expandedSection === "facilityType"}
                    onClick={() => toggleAccordion("facilityType")}
                  >
                    <div className="accordion-label">
                      <ChairIcon color="#687079" />
                      <span>{t("facilityType")}</span>
                      {filters.facilityType.length > 0 && (
                        <span style={{ fontWeight: 700 }}>{filters.facilityType[0]}</span>
                      )}
                    </div>
                    <div className="accordion-arrow">
                      <ArrowDown2Icon />
                    </div>
                  </StyledAccordionHeader>
                  {expandedSection === "facilityType" && (
                    <StyledAccordionContent>
                      {facilityTypes.map((type) => {
                        const isSelected = filters.facilityType.includes(type)
                        return (
                          <StyledMobileCheckboxItem
                            key={type}
                            onClick={() => handleMobileFacilityType(type)}
                          >
                            <MultiSelectCheckbox $isSelected={isSelected}>
                              {isSelected && <Check2Icon />}
                            </MultiSelectCheckbox>
                            <span className="label-text">{type}</span>
                          </StyledMobileCheckboxItem>
                        )
                      })}
                    </StyledAccordionContent>
                  )}
                </StyledAccordionItem>

                <StyledAccordionItem $disabled={isOnlyOfficesSelected}>
                  <StyledAccordionHeader
                    $expanded={expandedSection === "date"}
                    onClick={() => !isOnlyOfficesSelected && toggleAccordion("date")}
                  >
                    <div className="accordion-label">
                      <Calendar3Icon color="#687079" />
                      <span>{t("when")}</span>
                      {filters.date && !isOnlyOfficesSelected && (
                        <span style={{ fontWeight: 700 }}>
                          {dateDisplayValue || dayjs(filters.date).format("DD.MM.YYYY")}
                        </span>
                      )}
                      {isOnlyOfficesSelected && (
                        <span style={{ fontWeight: 300, color: "#687079" }}>
                          {t("noDateNeeded")}
                        </span>
                      )}
                    </div>
                    {!isOnlyOfficesSelected && (
                      <div className="accordion-arrow">
                        <ArrowDown2Icon />
                      </div>
                    )}
                  </StyledAccordionHeader>
                  {expandedSection === "date" && (
                    <StyledAccordionContent>
                      {isOnlyOfficesSelected ? (
                        <StyledNoDateMessage>
                          <Calendar3Icon color="#687079" />
                          {t("noDateNeeded")}
                        </StyledNoDateMessage>
                      ) : (
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="he">
                          <StyledMobileCalendar>
                            <StaticDatePicker
                              dayOfWeekFormatter={(day: string) => `${day.replace(/׳/g, "")}׳`}
                              value={filters.date ? dayjs(filters.date) : null}
                              onChange={(date: any) => {
                                setFilters((prev) => ({
                                  ...prev,
                                  date: date ? (date as Dayjs).format("YYYY-MM-DD") : "",
                                }))
                                setShowError(false)
                              }}
                              minDate={dayjs()}
                              shouldDisableDate={
                                isMeetingRoomSelected
                                  ? hostAvailability?.shouldDisableDate
                                  : undefined
                              }
                              slots={{
                                actionBar: () => null,
                                toolbar: () => null,
                                calendarHeader: CustomCalendarHeader,
                                ...(isMeetingRoomSelected ? { day: StrikethroughPickersDay } : {}),
                              }}
                              slotProps={{
                                day: {
                                  sx: datePickerSlotProps.popper.sx["& .MuiPickersDay-root"],
                                },
                              }}
                            />
                            {isMeetingRoomSelected && (
                              <>
                                <StyledCalendarTimeRow>
                                  <StyledTimeField>
                                    <StyledTimeLabel>{t("startTime")}</StyledTimeLabel>
                                    <InlineTimeDropdown
                                      value={filters.startTime}
                                      placeholder={t("selectTime")}
                                      options={START_TIME_SLOTS}
                                      disabledOptions={disabledTimeSlots}
                                      onChange={(val) => {
                                        setFilters((prev) => ({ ...prev, startTime: val }))
                                        setShowError(false)
                                      }}
                                    />
                                  </StyledTimeField>
                                  <StyledTimeField>
                                    <StyledTimeLabel>{t("endTime")}</StyledTimeLabel>
                                    <InlineTimeDropdown
                                      value={filters.endTime}
                                      placeholder={t("selectTime")}
                                      options={endTimeSlots}
                                      disabledOptions={disabledTimeSlots}
                                      onChange={(val) => {
                                        setFilters((prev) => ({ ...prev, endTime: val }))
                                        setShowError(false)
                                      }}
                                    />
                                  </StyledTimeField>
                                </StyledCalendarTimeRow>
                                {duration && (
                                  <StyledDuration
                                    style={{
                                      padding: "0 16px 4px",
                                      textAlign: "center",
                                    }}
                                  >
                                    {t("duration")}{" "}
                                    <span style={{ fontWeight: 700 }}>{duration}</span>
                                  </StyledDuration>
                                )}
                                {filters.startTime && filters.endTime && (
                                  <StyledConfirmButton onClick={() => setExpandedSection(null)}>
                                    {t("confirm")}
                                  </StyledConfirmButton>
                                )}
                              </>
                            )}
                          </StyledMobileCalendar>
                        </LocalizationProvider>
                      )}
                    </StyledAccordionContent>
                  )}
                </StyledAccordionItem>

                <StyledAccordionItem>
                  <StyledAccordionHeader
                    $expanded={expandedSection === "numberOfPeople"}
                    onClick={() => toggleAccordion("numberOfPeople")}
                  >
                    <div className="accordion-label">
                      <UserIcon color="#687079" />
                      <span>{t("howManyPeople")}</span>
                      {filters.numberOfPeople.length > 0 && (
                        <span style={{ fontWeight: 700 }}>{filters.numberOfPeople[0]}</span>
                      )}
                    </div>
                    <div className="accordion-arrow">
                      <ArrowDown2Icon />
                    </div>
                  </StyledAccordionHeader>
                  {expandedSection === "numberOfPeople" && (
                    <StyledAccordionContent>
                      {peopleOptions.map((opt) => {
                        const isSelected = filters.numberOfPeople.includes(opt)
                        return (
                          <StyledMobileCheckboxItem
                            key={opt}
                            onClick={() => handleMobileCheckbox("numberOfPeople", opt)}
                          >
                            <MultiSelectCheckbox $isSelected={isSelected}>
                              {isSelected && <Check2Icon />}
                            </MultiSelectCheckbox>
                            <span className="label-text">{opt}</span>
                          </StyledMobileCheckboxItem>
                        )
                      })}
                    </StyledAccordionContent>
                  )}
                </StyledAccordionItem>
              </StyledDrawerScroll>

              <StyledDrawerCTA>
                <StyledDrawerClearButton onClick={handleClear}>
                  {t("clearAllDrawer")}
                </StyledDrawerClearButton>
                <StyledDrawerSearchButton
                  $active={!!hasAnyFilter}
                  onClick={() => {
                    handleSearch()
                    if (hasAnyFilter) setDrawerOpen(false)
                  }}
                >
                  {t("search")}
                </StyledDrawerSearchButton>
              </StyledDrawerCTA>
            </StyledDrawer>
          </>
        )}
      </StyledMobile>
    </Box>
  )
}

export default FacilitiesSearchBar
